/**
 * Pure pan-decision logic for bringing a selected detail entity (e.g. a meter
 * chosen from the transformer panel's Connected Meters list) into view.
 *
 * Kept free of Leaflet so it can be unit-tested at the handler level — the
 * PanToHandler in MapView adapts the live map to {@link PanViewport} and applies
 * the returned {@link PanDecision}.
 */

/** Sensible close-in zoom for a point detail when it is off-screen. Mirrors the
 *  meter zoom level used for search picks (searchZoom POINT_ZOOM.meter is 18;
 *  17 keeps a little surrounding context for a navigated-to meter). */
export const DETAIL_PAN_ZOOM = 17

export type PanDecision =
  | { action: 'none' }
  | { action: 'pan' }
  | { action: 'fly'; zoom: number }

export interface PanViewport {
  /** Whether the point is inside the current map bounds. */
  contains: (lat: number, lng: number) => boolean
  /** Current map zoom. */
  zoom: number
}

/**
 * Decide how to move the map to reveal a detail entity.
 *
 * - `force` (the "Show on map" button): always recenter. Pan while keeping zoom
 *   when the point is already visible; fly + zoom in when it is off-screen.
 * - auto (a new selection): only move when the point is off-screen, so clicking
 *   an already-visible meter never yanks the map around.
 *
 * When flying, the current zoom is kept if it is already closer than
 * {@link DETAIL_PAN_ZOOM} (never zoom *out* just to reveal a point).
 */
export function decideDetailPan(
  target: { lat: number; lng: number },
  view: PanViewport,
  force: boolean,
): PanDecision {
  const visible = view.contains(target.lat, target.lng)
  if (visible) {
    return force ? { action: 'pan' } : { action: 'none' }
  }
  return { action: 'fly', zoom: Math.max(view.zoom, DETAIL_PAN_ZOOM) }
}
